import { useCallback, useRef } from 'react';
import { useAuth, useUser } from '@clerk/react';

export type SessionStatus = 'carregando' | 'anonimo' | 'autenticado';

export interface Session {
  status: SessionStatus;
  userId: string | null;
  displayName: string;
  getToken: () => Promise<string | null>;
}

/**
 * The only place besides the account menu that touches Clerk.
 *
 * Keeping the dependency behind one module means tests mock a local file instead of the Clerk
 * package, and swapping the identity provider later is a single-file change.
 */
const nullToken = async () => null;

export function useSession(): Session {
  const { isLoaded, isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const stableGetToken = useCallback(() => getTokenRef.current(), []);

  if (!isLoaded) {
    return { status: 'carregando', userId: null, displayName: '', getToken: nullToken };
  }

  if (!isSignedIn) {
    return { status: 'anonimo', userId: null, displayName: '', getToken: nullToken };
  }

  return {
    status: 'autenticado',
    userId: user.id,
    displayName: user.firstName ?? user.primaryEmailAddress?.emailAddress ?? 'lojista',
    getToken: stableGetToken,
  };
}
